import BigNumber from 'bignumber.js'
import { Token } from 'config/types'
import useFullSizeModalRouteUpdater from 'hooks/useFullSizeModalRouteUpdater'
import Column from 'layout/Components/Column'
import { Trans, useTranslation } from 'react-i18next'
import { ModalBody, ModalHeader, ModalProps } from 'UIKit/AlchemyModal'
import { ModalSizeModeEnum } from 'UIKit/AlchemyModal/Modal'
import { filterTokenAmountSearch } from 'utils/token'
import { useFilterOptionsBySearch } from 'views/Payment/DesktopPayment/components/PaymentSelect/hooks'
import { PaymentPageEnums } from 'views/Payment/Menu'
import {
  extractPaymentTokenKey,
  useAvailableDepositTokens,
  useFetchOptionsCurrencyBalance,
} from '../../hooks/useTokens'
import {
  EmptyCurrencyResponse,
  EmptyMessageEnum,
  PAYMENT_MODAL_THEME,
  PaymentAppSearch,
  PaymentLabel,
  PaymentSuggestCurrencyHistory,
  StylePaymentModal,
} from '../styles'
import CurrencyRowItem from './CurrencyRowItem'
import { useStoredSearchToken } from './useStoredSearchToken'

const PaymentTokenSelectModal: React.FC<
  ModalProps<{
    selectedToken: Token
    onCurrencySelect: (token: Token) => void

    hideHUSDToken?: boolean
    isIncludeFiat?: boolean
    isIncludeAll?: boolean
    isAllowCloseAfterSelect?: boolean

    fetchBalance?: (token: Token) => Promise<BigNumber>
    hideNetwork?: boolean
    currencies?: Token[]
    allCurrencies?: Token[]
    from: PaymentPageEnums
  }>
> = ({
  data: {
    onCurrencySelect,
    selectedToken,
    hideHUSDToken,
    isIncludeFiat,
    isIncludeAll,

    fetchBalance,
    hideNetwork,
    currencies: currenciesProps = [],
    allCurrencies: allCurrenciesProps = [],
    isAllowCloseAfterSelect = false,

    from,
  },
  onDismiss,
}) => {
  const { t } = useTranslation()
  const storedSearch = useStoredSearchToken(from)

  const onBack = useFullSizeModalRouteUpdater('PTS', onDismiss)

  const {
    currenciesGroupSameCode: currenciesGroupSameCodeConfig,
    isValidAddress,
    currencies: allCurrenciesConfig,
  } = useAvailableDepositTokens({
    onCurrencySelect,
    selectedCurrency: selectedToken,
    hideHUSDToken,
    isIncludeFiat,
    from,
  })

  const currenciesControl = isIncludeAll
    ? allCurrenciesConfig
    : currenciesProps.length > 0
    ? currenciesProps
    : currenciesGroupSameCodeConfig

  const allCurrenciesControl =
    isIncludeAll || !(allCurrenciesProps.length > 0) ? allCurrenciesConfig : allCurrenciesProps

  const {
    currencies,
    currenciesMap,
    fetchBalance: fetchBalanceControl,
  } = useFetchOptionsCurrencyBalance({
    curriencies: currenciesControl,
    fetchTokenBalanceFn: fetchBalance,
    from,
  })

  const { onChange, options, typedValue } = useFilterOptionsBySearch({
    options: currencies,
    filterFn: filterTokenAmountSearch,
  })

  const handleChangeToken = (token) => {
    onCurrencySelect(token)

    if (isAllowCloseAfterSelect) {
      onBack()
    }
  }

  return (
    <StylePaymentModal sizeMode={ModalSizeModeEnum.Full} colorTheme={PAYMENT_MODAL_THEME} onDismiss={onBack}>
      <ModalHeader
        colorTheme={PAYMENT_MODAL_THEME}
        onBack={onBack}
        hideClose
        title={t('Select Coin')}
        titleAlign="center"
        useBackIcon
        hideStroke
      />
      <ModalBody overflowY="hidden" gap="20px">
        <PaymentAppSearch autoFocus={false} placeholder={t('Search Coins')} onValueChanged={onChange} />
        {!typedValue && (
          <PaymentSuggestCurrencyHistory
            hideNetwork={hideNetwork}
            control={storedSearch}
            onSelectKey={(str) => {
              const { tokenCode, network } = extractPaymentTokenKey(str)
              const selectedToken =
                allCurrenciesControl.find((p) => p.code === tokenCode && p.network === network) ||
                currenciesMap[str]?.token

              if (selectedToken) {
                handleChangeToken(selectedToken)
                storedSearch.record(selectedToken.code, selectedToken.address, selectedToken.network, hideNetwork)
              } else {
                storedSearch.remove(str)
              }
            }}
          />
        )}

        <Column flex={1} gap="12px">
          <PaymentLabel bold>
            <Trans>{t('List Coins')}</Trans>
          </PaymentLabel>
          <Column
            className="hide-scroll"
            gap="8px"
            overflowY="scroll"
            height={`calc(var(--screen-height) - ${storedSearch.tokens.length > 0 && !typedValue ? '255px' : '170px'})`}
          >
            {options.length > 0
              ? options.map((currency) => (
                  <CurrencyRowItem
                    key={`select-token-${currency.token?.code}-${currency.token?.network}`}
                    hideNetwork={hideNetwork}
                    selectedToken={selectedToken}
                    currency={currency.token}
                    amount={currency.amount}
                    from={from}
                    fetchBalance={isValidAddress ? fetchBalanceControl : undefined}
                    onSelect={() => {
                      storedSearch.record(
                        currency.token.code,
                        currency.token.address,
                        currency.token.network,
                        hideNetwork,
                      )
                      handleChangeToken(currency.token)
                    }}
                  />
                ))
              : typedValue && <EmptyCurrencyResponse message={EmptyMessageEnum.Currency} />}
          </Column>
        </Column>
      </ModalBody>
    </StylePaymentModal>
  )
}

export default PaymentTokenSelectModal
